import React, { Component, createRef } from "react";
import { connect } from "react-redux";
import Product from "./Product";
import NoResults from "./NoResults";
import { showSearch } from "../store/actions/productActions";
import { BRAND_NAME } from "../config/domain";

// ── Skeleton card ─────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="product-skeleton">
    <div className="product-skeleton__img" />
    <div className="product-skeleton__body">
      <div className="product-skeleton__line product-skeleton__line--name" />
      <div className="product-skeleton__line product-skeleton__line--size" />
      <div className="product-skeleton__line product-skeleton__line--price" />
    </div>
    <div className="product-skeleton__btn" />
  </div>
);

const SkeletonGroup = ({ count = 4, title }) => (
  <div className="products-group">
    <h3 className="category-title">
      <span className="product-skeleton__cat-title" style={{ width: title, display:'inline-block' }}/>
    </h3>
    {Array.from({ length: count }).map((_, i) => <SkeletonCard key={i} />)}
  </div>
);

const StoreSkeleton = () => (
  <div className="products">
    <SkeletonGroup count={4} title="120px" />
    <SkeletonGroup count={6} title="90px" />
    <SkeletonGroup count={5} title="140px" />
    <SkeletonGroup count={3} title="100px" />
  </div>
);

// ── Mobile detection for Load More ───────────────────────────
const isMobile = () => window.innerWidth <= 768;
const LOAD_MORE_STEP = 30;
const DESKTOP_STEP = 60; // desktop loads more per batch but still virtualizes

class Store extends Component {
  constructor(props) {
    super(props);
    this.state = {
      term: this.props.searchTerm,
      visibleCount: isMobile() ? LOAD_MORE_STEP : DESKTOP_STEP,
    };
    this.props.showSearch(true);

    this.categoryRefs = {};
    this._categoryOrder = []; // kept in sync after each render
    this.sentinelRef = React.createRef();
    this._observer = null;
  }

  scrollToCategory = (ref) => {
    if (!ref || !ref.current) return;
    const offset = 60;
    const elementPosition = ref.current.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;
    window.scrollTo({ top: offsetPosition, behavior: "smooth" });
  };

  // ── Spacebar → scroll to next category ──────────────────────
  _handleSpacebar = (e) => {
    // Only on store page (no search term, not in input/textarea/button)
    if (this.state.term) return;
    const tag = document.activeElement?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'BUTTON' || tag === 'SELECT') return;
    if (e.code !== 'Space') return;

    e.preventDefault();

    const order = this._categoryOrder;
    if (!order.length) return;

    const OFFSET = 60; // header height
    const scrollY = window.scrollY;

    // Find the first category whose top is strictly below current scroll
    let nextCat = null;
    for (const cat of order) {
      const ref = this.categoryRefs[cat.toLowerCase()];
      if (!ref?.current) continue;
      const top = ref.current.getBoundingClientRect().top + scrollY - OFFSET;
      if (top > scrollY + 10) { // +10px threshold to avoid same-section trigger
        nextCat = cat;
        break;
      }
    }

    if (nextCat) {
      // Scroll to next category
      this.scrollToCategory(this.categoryRefs[nextCat.toLowerCase()]);
    } else {
      // Already past last — scroll back to top (first category)
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  componentDidMount() {
    window.addEventListener('keydown', this._handleSpacebar);
    // Use IntersectionObserver on ALL devices to limit DOM size
    const step = isMobile() ? LOAD_MORE_STEP : DESKTOP_STEP;
    this._observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this.setState(s => {
            const next = s.visibleCount + step;
            return next <= s.visibleCount ? null : { visibleCount: next };
          });
        }
      },
      { rootMargin: '400px' }
    );
    if (this.sentinelRef.current) {
      this._observer.observe(this.sentinelRef.current);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this._handleSpacebar);
    if (this._observer) this._observer.disconnect();
  }

  componentDidUpdate(prevProps, prevState) {
    // Re-observe sentinel after render (it may have been re-mounted)
    if (this._observer && this.sentinelRef.current) {
      this._observer.disconnect();
      this._observer.observe(this.sentinelRef.current);
    }
    if (prevProps.searchTerm !== this.props.searchTerm) {
      this.setState({
        term: this.props.searchTerm,
        visibleCount: isMobile() ? LOAD_MORE_STEP : DESKTOP_STEP,
      });
    }

    if (prevProps.category !== this.props.category && this.props.category && !this.state.term) {
      const cat = this.props.category;
      // Спочатку показуємо всі items щоб категорія була в DOM
      // потім скролимо до неї
      this.setState({ visibleCount: Infinity }, () => {
        const ref = this.categoryRefs[cat.toLowerCase()];
        // Невелика затримка щоб DOM оновився після setState
        setTimeout(() => this.scrollToCategory(ref), 50);
      });
    }
  }

  render() {
    const { productList, favorites } = this.props;
    const { term } = this.state;

    // Show skeleton while products haven't loaded yet
    if (!productList || productList.length === 0) {
      return <StoreSkeleton />;
    }

    // Фільтруємо по пошуку
    let filteredProducts = productList.filter(product => {
      if (!term) return true;
      const haystack = product.name.toLowerCase();
      const needle   = term.toLowerCase().trim();

      // 1. Direct match
      if (haystack.includes(needle)) return true;

      // 2. All words present in any order (e.g. "red grapes" matches "Grapes Red")
      const words = needle.split(/\s+/).filter(Boolean);
      if (words.length > 1 && words.every(w => haystack.includes(w))) return true;

      // 3. Fuzzy — each search word matches if haystack contains a word
      //    within edit-distance 1 (typo tolerance)
      function editDist(a, b) {
        if (Math.abs(a.length - b.length) > 2) return 99;
        const dp = Array.from({length: a.length+1}, (_,i) =>
          Array.from({length: b.length+1}, (_,j) => i===0?j:j===0?i:0)
        );
        for (let i=1;i<=a.length;i++)
          for (let j=1;j<=b.length;j++)
            dp[i][j] = a[i-1]===b[j-1] ? dp[i-1][j-1]
              : 1+Math.min(dp[i-1][j], dp[i][j-1], dp[i-1][j-1]);
        return dp[a.length][b.length];
      }
      const haystackWords = haystack.split(/\s+/);
      const fuzzyMatch = words.every(sw =>
        sw.length >= 3 && haystackWords.some(hw => editDist(sw, hw) <= 1)
      );
      return fuzzyMatch;
    });

    // Фільтруємо по Favorites
    if (this.props.category === 'favorites') {
      filteredProducts = filteredProducts.filter(p => favorites.includes(p.id));
    }

    // Якщо є пошуковий термін АБО обрано Favorites — показуємо грід без категорій
    if (term || this.props.category === 'favorites') {
      const flatProducts = filteredProducts.sort((a, b) => a.name.localeCompare(b.name));
      const { visibleCount } = this.state;
      const visibleFlat = flatProducts.slice(0, visibleCount);
      const hasMoreFlat = visibleCount < flatProducts.length;
      const headingText = term
        ? `Search results for "${term}"`
        : `Favorites (${flatProducts.length})`;
      return (
        <div className="products-wrapper">
          {/* Прихований h1 для скрін-рідерів та heading hierarchy */}
          <h1 className="sr-only">{window._bvS?.brandName || BRAND_NAME} — Fresh Produce</h1>
          <h2 className="sr-only">{headingText}</h2>
          {flatProducts.length === 0 ? (
            <NoResults />
          ) : (
            <>
              <div className="products">
                {visibleFlat.map(product => (
                  <Product
                    key={product.id}
                    price={product.price}
                    itemName={product.name}
                    sizeName={product.quantity_type?.name}
                    image={product.image?.[0]?.url}
                    id={product.id}
                    quantity_single={product.quantity}
                    quantity_type={product.quantity_type?.name}
                    weight={product.weight}
                    organic={product.organic}
                    fromCart={false}
                  />
                ))}
              </div>
              {/* Sentinel — IntersectionObserver triggers load more */}
              {hasMoreFlat && (
                <div ref={this.sentinelRef} className="store-sentinel">
                  <div className="store-sentinel__spinner" />
                </div>
              )}
            </>
          )}
        </div>
      );
    }

    // Групуємо продукти по категорії, якщо search пустий
    const grouped = {};
    filteredProducts.forEach(product => {
      const cat = product.category?.name || "UNCATEGORIZED";
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(product);
    });

    const categoryOrder = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
    this._categoryOrder = categoryOrder; // keep ref for spacebar navigation

    // Load More для категорій — рахуємо загальну кількість видимих items
    const { visibleCount } = this.state;
    let totalShown = 0;
    let reachedLimit = false;

    const productsData = categoryOrder.map(cat => {
      if (reachedLimit) return null;
      if (!this.categoryRefs[cat.toLowerCase()]) {
        this.categoryRefs[cat.toLowerCase()] = createRef();
      }

      const sortedProducts = grouped[cat].sort((a, b) => a.name.localeCompare(b.name));

      // Показуємо цілу категорію якщо вона вміщається, або частину якщо ні
      const remaining = visibleCount - totalShown;
      const productsToShow = sortedProducts.slice(0, remaining);
      totalShown += productsToShow.length;
      if (totalShown >= visibleCount && visibleCount < filteredProducts.length) {
        reachedLimit = true;
      }

      return (
        <div
          key={`group-${cat}`}
          ref={this.categoryRefs[cat.toLowerCase()]}
          className="products-group"
        >
          <h3 className="category-title">{cat}</h3>
          {productsToShow.map(product => (
            <Product
              key={product.id}
              price={product.price}
              itemName={product.name}
              sizeName={product.quantity_type?.name}
              image={product.image?.[0]?.url}
              id={product.id}
              quantity_single={product.quantity}
              quantity_type={product.quantity_type?.name}
              weight={product.weight}
              organic={product.organic}
              fromCart={false}
            />
          ))}
        </div>
      );
    }).filter(Boolean);

    const totalVisible = Math.min(visibleCount, filteredProducts.length);
    const hasMore = totalVisible < filteredProducts.length;

    const view = productsData.length === 0 ? <NoResults /> : (
      <>
        <div className="products">{productsData}</div>
        {/* Sentinel — IntersectionObserver triggers load more */}
        {hasMore && (
          <div ref={this.sentinelRef} className="store-sentinel">
            <div className="store-sentinel__spinner" />
          </div>
        )}
      </>
    );

    return (
      <div className="products-wrapper">
        {/* SEO: h1 + h2 з ключовими словами для Google */}
        <h1 className="sr-only">{window._bvS?.brandName || BRAND_NAME} — Fresh Produce Wholesale Toronto</h1>
        <h2 className="sr-only">Fresh Vegetables, Fruits &amp; Wholesale Produce Delivery in Toronto, Ontario</h2>
        {view}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  category: state.category,
  productList: state.products,
  searchTerm: state.searchTerm,
  favorites: state.favorites,
});

export default connect(mapStateToProps, { showSearch })(Store);
